/**
 * KB-META
 * Purpose: Layout service
 * Role: ui.service
 * EntryPoints: constructor(), if(), onTransitionEnd(), onMenuToggle()
 * Inputs: _config.darkTheme:unknown
 * Outputs: 
 * Validation: 
 * Dependencies: 
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=service/layout.service; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Injectable, effect, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';

export interface layoutConfig {
    preset?: string;
    primary?: string;
    surface?: string | undefined | null;
    darkTheme?: boolean;
    menuMode?: string;
    fontSize?: number;
}

interface LayoutState {
    staticMenuDesktopInactive?: boolean;
    overlayMenuActive?: boolean;
    configSidebarVisible?: boolean;
    staticMenuMobileActive?: boolean;
    menuHoverActive?: boolean;
}

interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    private storageKey: string = 'partner-portal-cura:layout-config';
    private isLoadingFromStorage: boolean = false;

    private readonly FONT_SIZE_MIN = 12;
    private readonly FONT_SIZE_MAX = 24;

    _config: layoutConfig = {
        preset: 'Aura',
        primary: 'blue',
        surface: null,
        darkTheme: false,
        menuMode: 'static',
        fontSize: 14
    };

    _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false
    };

    layoutConfig = signal<layoutConfig>(this._config);

    layoutState = signal<LayoutState>(this._state);

    private configUpdate = new Subject<layoutConfig>();

    private overlayOpen = new Subject<any>();

    private menuSource = new Subject<MenuChangeEvent>();

    private resetSource = new Subject();

    menuSource$ = this.menuSource.asObservable();

    resetSource$ = this.resetSource.asObservable();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));

    isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);

    isDarkTheme = computed(() => this.layoutConfig().darkTheme);

    getPrimary = computed(() => this.layoutConfig().primary);

    getSurface = computed(() => this.layoutConfig().surface);

    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    transitionComplete = signal<boolean>(false);

    private initialized = false;

    constructor() {
        // Load any stored configuration before setting up effects
        this.loadConfigFromStorage();

        effect(() => {
            const config = this.layoutConfig();
            if (config) {
                this.onConfigUpdate();
            }
        });

        effect(() => {
            const config = this.layoutConfig();

            if (!this.initialized || !config) {
                this.initialized = true;
                return;
            }

            this.handleDarkModeTransition(config);
        });
    }

    private handleDarkModeTransition(config: layoutConfig): void {
        if ((document as any).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
            this.onTransitionEnd();
        }
    }

    private startViewTransition(config: layoutConfig): void {
        const transition = (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });

        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => {});
    }

    toggleDarkMode(config?: layoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

            if (this.layoutState().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive }));
        } else {
            this.layoutState.update((prev) => ({ ...prev, staticMenuMobileActive: !this.layoutState().staticMenuMobileActive }));

            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());
        // Persist configuration to storage when not in the middle of loading from storage
        if (!this.isLoadingFromStorage) {
            this.saveConfigToStorage();
        }
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.resetSource.next(true);
    }

    updateFontSize(size: number) {
        const nextSize = this.clampFontSize(size);
        if (typeof document !== 'undefined' && document?.documentElement) {
            document.documentElement.style.fontSize = `${nextSize}px`;
        }
        this.layoutConfig.update((state) => ({ ...state, fontSize: nextSize }));
    }

    /**
     * Set a user-scoped storage key and reload stored preferences if available.
     */
    setStorageKey(key: string) {
        if (!key || key === this.storageKey) return;
        this.storageKey = key;
        this.loadConfigFromStorage();
    }

    /**
     * Return a copy of the default configuration.
     */
    getDefaultConfig(): layoutConfig {
        return { ...{
            preset: 'Aura',
            primary: 'blue',
            surface: null,
            darkTheme: false,
            menuMode: 'static',
            fontSize: 14
        } };
    }

    /**
     * Reset to default configuration and persist it.
     */
    resetToDefault() {
        const defaults = this.getDefaultConfig();
        this.isLoadingFromStorage = true;
        this.layoutConfig.set({ ...defaults });
        this.isLoadingFromStorage = false;
        this.saveConfigToStorage();
        this.updateFontSize(defaults.fontSize || 14);
        this.toggleDarkMode(defaults);
    }

    private saveConfigToStorage() {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.setItem(this.storageKey, JSON.stringify(this.layoutConfig()));
            }
        } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
    }

    private loadConfigFromStorage() {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return;
            const raw = window.localStorage.getItem(this.storageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw) as layoutConfig;
            if (parsed && typeof parsed === 'object') {
                this.isLoadingFromStorage = true;
                const next = { ...this._config, ...parsed } as layoutConfig;
                next.fontSize = this.clampFontSize(next.fontSize || this._config.fontSize || this.FONT_SIZE_MIN);
                this.layoutConfig.set(next);
                this.isLoadingFromStorage = false;
                // Apply immediate side effects
                this.updateFontSize(next.fontSize || this._config.fontSize || this.FONT_SIZE_MIN);
                this.toggleDarkMode(next);
            }
        } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
    }

    private clampFontSize(size: number | null | undefined): number {
        const numeric = typeof size === 'number' ? size : this.FONT_SIZE_MIN;
        return Math.min(Math.max(Math.round(numeric), this.FONT_SIZE_MIN), this.FONT_SIZE_MAX);
    }
}
